/**
 * Why a committee screen could not load, in the server's own words.
 *
 * These four screens are gated twice over, and the two gates do not agree. The
 * router lets a super_admin onto any of them (RequirePermission bypasses every
 * role restriction for that role), while the API asks a narrower question — is
 * this account ON the Membership Committee, resolved from the ACTIVE executive
 * set — and answers 403 when it is not. "Failed to load" is what that looked
 * like from the page, which reads as a broken screen rather than as an answer.
 *
 * So the message is shown. "You are not on the Membership Committee" is a fact
 * about the account, not a fault, and it is the difference between a mystery
 * and a two-second diagnosis.
 */
export default function LoadError({
  error,
  onRetry,
}: {
  error: unknown;
  onRetry: () => void;
}) {
  const e = error as any;
  // RTK Query carries the parsed body on `data` and the HTTP code on `status`;
  // a network failure has neither, only a bare `error` string.
  const status = typeof e?.status === "number" ? e.status : null;
  const said = e?.data?.message ?? (typeof e?.error === "string" ? e.error : null);

  // 403 is the common one and it is not a failure to load anything — the
  // request arrived, was understood, and was refused.
  const denied = status === 403;

  return (
    <div
      className={`rounded-2xl border p-4 text-sm ${
        denied
          ? "border-amber-200 bg-amber-50 text-amber-900"
          : "border-red-200 bg-red-50 text-red-700"
      }`}
    >
      <p className="font-semibold">
        {denied ? "You do not have access to this screen." : "Failed to load."}
      </p>

      {said ? <p className="mt-1">{said}</p> : null}

      {/* The code, because a 403 and a 500 are somebody else's problem to fix
          and knowing which one it is decides whose. */}
      {status ? <p className="mt-1 text-xs opacity-70">HTTP {status}</p> : null}

      {/* Retrying a refusal just repeats it. */}
      {!denied && (
        <button onClick={onRetry} className="mt-2 underline">
          retry
        </button>
      )}
    </div>
  );
}
